import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "chameleon/lib/components";

import {
  lpmPaymentCancelledAtom,
  paymentMessagesAtom,
  paymentRequirementsAtom,
} from "checkout-experience/atoms/payment-atoms";
import {
  modalLpmButtons,
  modalLpmMessage,
  modalLpmMessageWrapper,
} from "checkout-experience/components/modal-lpm/modal-lpm.css";
import { Modal } from "checkout-experience/components/modal/modal";
import LpmIcon from "checkout-experience/components/payment-method/pay-options/lpm/lpm-icon/lpm-icon";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import PopupWindow from "checkout-experience/components/popup-window/popup-window";
import { BrowserSettings } from "checkout-query-client/api/types";

import { PayResponse_DebugInfo } from "frontend-client/export";

interface ModalLpmProps {
  src: string | undefined;
  browserSettings: BrowserSettings;
  setBrowserSettings: Dispatch<SetStateAction<BrowserSettings>>;
}

export const ModalLpm = ({
  src,
  browserSettings,
  setBrowserSettings,
}: ModalLpmProps) => {
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { t } = useTranslation("combined");
  const [openedWindow, setOpenedWindow] = useState<Window>();
  const paymentRequirements = useAtomValue(paymentRequirementsAtom);
  const setLpmPaymentCancelled = useSetAtom(lpmPaymentCancelledAtom);
  const [paymentMessages, setPaymentMessages] = useAtom(paymentMessagesAtom);

  useEffect(() => {
    // Open modal
    if (!browserSettings?.isModalOpen && paymentRequirements?.lpm) {
      setBrowserSettings((prevSettings) => ({
        ...prevSettings,
        isModalOpen: true,
      }));
    }
  }, [paymentRequirements]);

  const handleCloseModal = (completed?: boolean) => {
    setBrowserSettings((prevSettings) => ({
      ...prevSettings,
      isModalOpen: false,
    }));
    if (!completed) {
      setLpmPaymentCancelled(true);
    }
  };

  const closeModal = () => {
    handleCloseModal(false);
  };

  useEffect(() => {
    // useEffect for lpm completion listener
    const postMessageListener = (event: {
      type: string;
      data: {
        event: string;
        payload: { status: string; debugInfo: PayResponse_DebugInfo };
      };
    }) => {
      if (event?.data?.event === "Lpm-completion") {
        setPaymentMessages({
          lpmCompletionMessage: event.data.payload,
        });
      }
      return;
    };
    window.addEventListener("message", postMessageListener);

    // cleanup on unmount
    return () => {
      window.removeEventListener("message", postMessageListener);
    };
  }, []);

  useEffect(() => {
    if (
      paymentMessages?.lpmCompletionMessage?.status === "STATUS_SUCCESS" ||
      paymentMessages?.lpmCompletionMessage?.status === "STATUS_FAILURE" ||
      paymentMessages?.lpmCompletionMessage?.status === "STATUS_PENDING"
    ) {
      handleCloseModal(true);
    }
  }, [paymentMessages]);

  const onRefocusPopup = () => {
    openedWindow?.focus();
  };

  return (
    <>
      <PopupWindow
        url={src}
        isOpen={browserSettings?.isModalOpen ?? false}
        onClose={closeModal}
        onOpen={setOpenedWindow}
      />
      <Modal
        data-testid="modal-lpm"
        wrapperId="modal-lpm-wrapper"
        closeModalHandler={closeModal}
        isModalOpen={browserSettings?.isModalOpen}
        title={""}
        fullScreen
      >
        <div
          className={modalLpmMessageWrapper}
          data-testid={"modal-lpm-content"}
        >
          <div className={modalLpmMessage}>
            <LpmIcon paymentType={paymentOption?.paymentType} />
            <div>
              {t("cannotSeeTheSecureBrowser", {
                popUpName: t(paymentOption?.paymentType ?? ""),
              })}
            </div>
            <div className={modalLpmButtons}>
              <Button
                variant={"text"}
                onClick={onRefocusPopup}
                text={t("reopenPopup")}
                id={"lpm-refocus-popup"}
              />
              <p>{t("or")}</p>
              <Button
                variant={"text"}
                onClick={closeModal}
                text={t("chooseAnotherPaymentMethod")}
                id={"lpm-close-modal"}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
